/**
 * 登录相关 API
 */

import http from '~/utils/http'
import type { HttpResponse, RequestConfig } from '~/types/http'
import type {
  GetEmailCodeParams,
  VerifyEmailCodeParams,
  CheckEmailRegParams,
  EmailRegisterParams,
  EmailLoginParams,
  FindPasswordParams,
  LoginResponse,
  GoogleLoginParams,
  MicrosoftLoginResponse,
  MicrosoftCallbackParams
} from '~/types/login'

/**
 * 获取邮箱验证码
 */
export async function getEmailCode(params: GetEmailCodeParams, config?: Partial<RequestConfig>): Promise<HttpResponse> {
  return http.get('/user/getEmailCode_hw', {
    params,
    showSuccess: false,
    loading: false,
    ...config
  })
}

/**
 * 验证邮箱验证码
 */
export async function verifyEmailCode(params: VerifyEmailCodeParams, config?: Partial<RequestConfig>): Promise<HttpResponse> {
  return http.get('/auth/checkCodeByEmail_hw', {
    params,
    showSuccess: false,
    loading: false,
    ...config
  })
}

/**
 * 检查邮箱是否已注册
 * @return
 * 1: 已注册
 * 0：未注册
 */
export async function checkEmailRegistered(params: CheckEmailRegParams, config?: Partial<RequestConfig>): Promise<HttpResponse<1 | 0>> {
  return http.get('/user/checkEmailReg', {
    params,
    showSuccess: false,
    loading: false,
    ...config
  })
}

/**
 * 邮箱注册
 */
export async function emailRegister(params: EmailRegisterParams, config?: Partial<RequestConfig>): Promise<HttpResponse<LoginResponse>> {
  return http.post('/auth/register_hw', params, {
    showSuccess: true,
    loading: true,
    ...config
  })
}

/**
 * 邮箱密码登录
 */
export async function emailLogin(params: EmailLoginParams, config?: Partial<RequestConfig>): Promise<HttpResponse<LoginResponse>> {
  return http.post('/auth/login_hw', params, {
    showSuccess: true,
    loading: true,
    showError: false,
    ...config
  })
}

/**
 * 找回密码
 */
export async function findPassword(params: FindPasswordParams, config?: Partial<RequestConfig>): Promise<HttpResponse<{ success: boolean }>> {
  return http.post('/auth/findPassword_hw', params, {
    showSuccess: true,
    loading: true,
    ...config
  })
}

/**
 * 谷歌登录
 * @param params - 请求参数对象
 * @param params.keyword - 推广关键字
 * @param params.invitedBy - 邀请码
 * @param params.idToken - 调起谷歌账号登录后，谷歌返回的 credential 凭证
 * @param config - 可选的请求配置，用于覆盖默认配置
 */
export async function googleLogin(
  params: GoogleLoginParams,
  config?: Partial<RequestConfig>
): Promise<HttpResponse<LoginResponse>> {
  return http.post('/auth/google_login_hw', params, {
    showSuccess: true,
    loading: true,
    ...config
  })
}

/**
 * 微软登录，第一步获取登录 URL
 */
export async function microsoftLogin(config?: Partial<RequestConfig>): Promise<HttpResponse<MicrosoftLoginResponse>> {
  return http.get('/auth/microsoft/login', {
    showSuccess: true,
    loading: true,
    ...config
  })
}

/**
 * 微软登录，第二步，登录成功回调
 * @param params - 请求参数对象
 * @param params.code - 微软登录后给的 code
 * @param params.state - 微软登录后给的 state
 * @param params.keyword - 推广关键字（可选）
 * @param params.invitedBy - 邀请码（可选）
 * @param config - 可选的请求配置，用于覆盖默认配置
 */
export async function microsoftCallback(
  params: MicrosoftCallbackParams,
  config?: Partial<RequestConfig>
): Promise<HttpResponse<LoginResponse>> {
  return http.get('/auth/microsoft/callback', {
    params,
    showSuccess: true,
    loading: true,
    ...config
  })
}
